/**
 * Readers Service
 *
 * API for Beyond Readers books. All users read; teachers/admins manage.
 */

import type { Book, CreateBookRequest, UpdateBookRequest } from "../types/material";
import api from "./api";

function toCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toCamelCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    result[camelKey] =
      typeof value === "object" && value !== null
        ? toCamelCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    result[snakeKey] = value;
  }
  return result;
}

export async function getBooks(year?: number): Promise<Book[]> {
  const response = await api.get("/books/", {
    params: { year },
  });
  const data = Array.isArray(response.data)
    ? response.data
    : (response.data?.results ?? []);
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as Book[];
}

export async function createBook(data: CreateBookRequest): Promise<Book> {
  const response = await api.post(
    "/books/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as Book;
}

export async function updateBook(
  id: string,
  data: UpdateBookRequest,
): Promise<Book> {
  const response = await api.patch(
    `/books/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as Book;
}

export async function deleteBook(id: string): Promise<void> {
  await api.delete(`/books/${id}/`);
}
